import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Home        from './pages/Home';
import Documents   from './pages/Documents';
import Upload      from './pages/Upload';
import Scheduling  from './pages/Scheduling';
import Users       from './pages/Users';
import Settings    from './pages/Settings';
import ActivityLog from './pages/ActivityLog';
import Login       from './pages/Login';
import Setup       from './pages/Setup';

export const API_BASE = process.env.REACT_APP_API_BASE || 'https://xboard.webxpay.dev';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

// ── Permission helpers ────────────────────────────────────────────────────────
// Superadmin always passes. Otherwise we look inside user.permissions.
const isSuper = (user) => user?.role === 'superadmin';

const can = (user, group, key) => {
  if (isSuper(user)) return true;
  const p = user?.permissions || {};
  if (key === null) return !!p[group];          // single boolean (e.g. upload)
  return !!(p[group] && p[group][key]);          // nested (e.g. documents.view)
};

const canAny = (user, group, keys) => {
  if (isSuper(user)) return true;
  return keys.some(k => can(user, group, k));
};

// What each nav item requires. fn returns true if the user may see it.
const NAV = [
  { path: '/home',       label: 'Home',       show: () => true },
  { path: '/documents',  label: 'Documents',  show: () => true },
  { path: '/upload',     label: 'Upload',     show: (u) => can(u, 'upload', null) },
  { path: '/scheduling', label: 'Scheduling', show: (u) => canAny(u, 'scheduling', ['schedule', 'edit', 'cancel']) },
  { path: '/users',      label: 'Users',      show: (u) => canAny(u, 'manage_users', ['add', 'edit', 'delete']) },
  { path: '/activity',   label: 'Activity Log', show: (u) => isSuper(u) || can(u, 'activity', null) },
  { path: '/settings',   label: 'Settings',   show: () => true },
];

function ProtectedRoute({ children, allow }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (allow && !allow(user)) return <Navigate to="/home" replace />;
  return children;
}

function Sidebar({ onLogout }) {
  const location = useLocation();
  const { user } = useAuth();

  const navItems = useMemo(
    () => NAV.filter(item => item.show(user)),
    [user],
  );

  const roleLabel = isSuper(user) ? 'Super Admin' : (user.role || 'Member');

  return (
    <div style={{
      width: '220px', background: '#22272D', padding: '24px 16px',
      display: 'flex', flexDirection: 'column', gap: '4px',
      position: 'fixed', height: '100vh', justifyContent: 'space-between',
    }}>
      <div>
        <Link to="/home" style={{ textDecoration: 'none' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '0 8px 20px', borderBottom: '0.5px solid rgba(255,255,255,0.08)',
            marginBottom: '8px',
          }}>
            <img src="/Xicon.png" alt="X Meeting" style={{ width: '28px' }} />
            <span style={{
              fontFamily: 'Bebas Neue, sans-serif', fontSize: '26px',
              fontWeight: '400', color: '#F4FAFF', letterSpacing: '2px',
            }}>
              MEETING
            </span>
          </div>
        </Link>

        {navItems.map(item => {
          const active = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} style={{
              padding: '9px 12px', borderRadius: '8px', fontSize: '13px',
              color:      active ? '#F1FF5C' : '#AAB0B8',
              background: active ? 'rgba(241,255,92,0.1)' : 'transparent',
              textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px',
              fontFamily: 'Manrope, sans-serif',
            }}>
              <span style={{
                width: '7px', height: '7px', borderRadius: '50%',
                background: active ? '#F1FF5C' : '#AAB0B8',
                flexShrink: 0, display: 'inline-block',
              }} />
              {item.label}
            </Link>
          );
        })}
      </div>

      <div>
        <div style={{
          padding: '12px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)',
          marginBottom: '8px',
        }}>
          <div style={{ fontSize: '13px', color: '#F4FAFF', fontWeight: '500' }}>{user.name}</div>
          <div style={{ fontSize: '11px', color: '#AAB0B8' }}>{roleLabel}</div>
        </div>
        <button type="button" onClick={onLogout} style={{
          width: '100%', padding: '9px', borderRadius: '8px',
          background: 'transparent', border: '0.5px solid #3D4349',
          color: '#AAB0B8', fontSize: '13px', fontFamily: 'Manrope, sans-serif',
          cursor: 'pointer',
        }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function MainApp() {
  const [user,       setUser]       = useState(null);
  const [needsSetup, setNeedsSetup] = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  const [appError,   setAppError]   = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const initApp = async () => {
      try {
        const [setupRes] = await Promise.all([
          axios.get(`${API_BASE}/api/setup/status`),
          axios.get(`${API_BASE}/api/me`, { withCredentials: true })
            .then(res => setUser(res.data))
            .catch(() => setUser(null)),
        ]);
        setNeedsSetup(setupRes.data.needs_setup);
      } catch {
        setAppError('Unable to connect to server. Please make sure the backend is running.');
        setNeedsSetup(false);
      } finally {
        setAppLoading(false);
      }
    };
    initApp();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE}/api/logout`, {}, { withCredentials: true });
    } catch { /* session already gone */ }
    setUser(null);
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) { setUser(null); navigate('/'); }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  const handleLogin = useCallback((userData) => {
    setUser(userData); navigate('/home');
  }, [navigate]);

  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    const resetTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(handleLogout, IDLE_TIMEOUT_MS);
    };
    resetTimer();
    IDLE_EVENTS.forEach(evt => window.addEventListener(evt, resetTimer, { passive: true }));
    return () => {
      clearTimeout(idleTimerRef.current);
      IDLE_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user, handleLogout]);

  const authValue = useMemo(() => ({ user, setUser }), [user]);

  if (appLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#22272D', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif', fontSize: '14px' }}>
        Loading…
      </div>
    );
  }

  if (appError) {
    return (
      <div style={{ minHeight: '100vh', background: '#22272D', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', fontFamily: 'Manrope, sans-serif' }}>
        <div style={{ fontSize: '14px', color: '#FF766B' }}>{appError}</div>
        <button type="button" onClick={() => window.location.reload()} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#F1FF5C', color: '#22272D', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>Retry</button>
      </div>
    );
  }

  if (needsSetup) {
    return (
      <AuthContext.Provider value={authValue}>
        <Setup onComplete={(userData) => { setNeedsSetup(false); if (userData) setUser(userData); }} />
      </AuthContext.Provider>
    );
  }

  if (!user) {
    return (
      <AuthContext.Provider value={authValue}>
        <Login onLogin={handleLogin} />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={authValue}>
      <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
        <Sidebar onLogout={handleLogout} />
        <div style={{ marginLeft: '220px', flex: 1, padding: '32px', background: '#F4FAFF' }}>
          <Routes>
            <Route path="/"            element={<Navigate to="/home" replace />} />
            <Route path="/home"        element={<ProtectedRoute><Home user={user} /></ProtectedRoute>} />
            <Route path="/documents"   element={<ProtectedRoute><Documents user={user} /></ProtectedRoute>} />
            <Route path="/upload"      element={<ProtectedRoute allow={(u) => can(u, 'upload', null)}><Upload /></ProtectedRoute>} />
            <Route path="/scheduling"  element={<ProtectedRoute allow={(u) => canAny(u, 'scheduling', ['schedule','edit','cancel'])}><Scheduling /></ProtectedRoute>} />
            <Route path="/users"       element={<ProtectedRoute allow={(u) => canAny(u, 'manage_users', ['add','edit','delete'])}><Users user={user} /></ProtectedRoute>} />
            <Route path="/activity"    element={<ProtectedRoute allow={(u) => isSuper(u) || can(u, 'activity', null)}><ActivityLog user={user} /></ProtectedRoute>} />
            <Route path="/settings"    element={<ProtectedRoute><Settings user={user} onUserUpdate={setUser} /></ProtectedRoute>} />
            <Route path="*"            element={<Navigate to="/home" replace />} />
          </Routes>
        </div>
      </div>
    </AuthContext.Provider>
  );
}

function App() {
  return <Router><MainApp /></Router>;
}

export default App;